import { FC, useMemo } from "react";
import { Drawer } from "shared/ui/drawer";
import {
    NewHiveBody,
    NewHiveFooter,
    NewHiveHead,
    NewHiveRow,
    NewHiveTitle,
    NewHiveWrapper,
} from "features/hives/ui/hivesNew/styles";
import Button from "@atomaro/ui-kit/components/Button/Button/Button";
import { CloseIcon } from "shared/ui/icons";
import { toRem } from "shared/helpers";
import { Spacer } from "shared/ui/spacer";
import { Controller } from "react-hook-form";
import { Input } from "@atomaro/ui-kit";
import ColorPicker from "features/hives/ui/hivesNew/ColorPicker";
import { markColors } from "features/hives/ui/hivesTable/HiveMarkCell";
import Select from "@atomaro/ui-kit/components/Select/Select";
import { useEditHive } from "features/hive/lib/useEditHive.ts";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    isMobile?: boolean;
};

export const HiveEdit: FC<Props> = ({ isOpen, onClose, isMobile }) => {
    const {
        control,
        errors,
        isSubmitting,
        onYearChange,
        onMarkNumberChange,
        onMarkColorChange,
        onBreedChange,
        handleSubmit,
        onSubmit,
        doReset,
        isReady,
    } = useEditHive();

    const closeAndReset = () => {
        doReset();
        onClose();
    };

    const submitAndClose = async (form: any) => {
        const res = await onSubmit(form);
        if (res) closeAndReset();
    };

    const years = useMemo(() => {
        const y = new Date().getFullYear();
        return Array.from({ length: 5 }, (_, i) => y - i).map((v) => ({
            key: String(v),
            value: String(v),
        }));
    }, []);

    return (
        <Drawer isOpen={isOpen} position={isMobile ? "bottom" : "right"} onClose={closeAndReset}>
            <NewHiveWrapper onSubmit={handleSubmit(submitAndClose)} className={isSubmitting || !isReady ? "_skeleton" : ""}>
                <NewHiveHead $isMobile={isMobile}>
                    <Button
                        iconPrefix={<CloseIcon />}
                        variant={isMobile ? "ghost" : "secondary"}
                        colorScheme="neutral"
                        style={{ alignSelf: "flex-end", width: isMobile ? toRem(24) : toRem(36) }}
                        size={isMobile ? "s" : "m"}
                        onClick={closeAndReset}
                    />
                    <NewHiveTitle>Данные матки</NewHiveTitle>
                    {isMobile && <Spacer width={toRem(24)} height={toRem(24)} />}
                </NewHiveHead>

                {isReady && (
                    <NewHiveBody>
                        <NewHiveRow $alignItems="center">
                            <span>Год матки</span>
                            <Controller
                                name="uterusYear"
                                control={control}
                                rules={{ required: { value: true, message: "Выберите год матки улья" } }}
                                render={({ field }) => (
                                    <Select
                                        size="m"
                                        placeholder="Выберите год"
                                        items={years}
                                        placement="bottom"
                                        defaultValue={String(field.value ?? "")}
                                        onChange={(v) => {
                                            field.onChange(v ? Number(v) : null);
                                            onYearChange(v ? Number(v) : null);
                                        }}
                                        error={errors.uterusYear?.message}
                                        dropdownMenuStyle={{ maxHeight: 300, overflowY: "auto" }}
                                    />
                                )}
                            />
                        </NewHiveRow>

                        <NewHiveRow $alignItems="center">
                            <span>Номер метки</span>
                            <Controller
                                name="uterusMark"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        name={field.name}
                                        value={String(field.value ?? "")}
                                        size="m"
                                        placeholder="Номер метки"
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/[^\d]/g, "");
                                            field.onChange(val);
                                            onMarkNumberChange(val);
                                        }}
                                    />
                                )}
                            />
                        </NewHiveRow>

                        <NewHiveRow $alignItems="center">
                            <span>Цвет метки</span>
                            <Controller
                                name="uterusMarkColor"
                                control={control}
                                render={({ field }) => {
                                    const id = Number(field.value ?? 0);
                                    const initialMainColor = markColors.find((m) => m.id === id)?.mainColor || markColors[0]?.mainColor;

                                    return (
                                        <ColorPicker
                                            items={markColors.map((c) => c.mainColor)}
                                            initial={initialMainColor}
                                            onChange={(cssVar) => {
                                                const nextId = markColors.find((m) => m.mainColor === cssVar)?.id ?? 0;
                                                field.onChange(nextId);
                                                onMarkColorChange(nextId as number);
                                            }}
                                        />
                                    );
                                }}
                            />
                        </NewHiveRow>

                        <NewHiveRow $alignItems="center">
                            <span>Порода</span>
                            <Controller
                                name="breed"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        name={field.name}
                                        value={String(field.value ?? "")}
                                        size="m"
                                        placeholder="Карника"
                                        onChange={(e) => {
                                            field.onChange(e.target.value);
                                            onBreedChange(e.target.value);
                                        }}
                                    />
                                )}
                            />
                        </NewHiveRow>
                    </NewHiveBody>
                )}

                <NewHiveFooter>
                    <Button type="submit" label="Сохранить" disabled={isSubmitting || !isReady} />
                    <Button label="Отменить" variant="ghost" colorScheme="neutral" onClick={closeAndReset} />
                </NewHiveFooter>
            </NewHiveWrapper>
        </Drawer>
    );
};
